import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, map, startWith, tap } from 'rxjs/operators';
import { EstimateType, RevenueAreaType, UserStateInfo } from '../../client-api.service';
import { CommentsStateService } from '../../comments/comments-state.service';
import { MenuTrayStateService } from '../../menu-tray/menu-tray-state.service';
import { GLOBAL, IGlobalSettings } from '../../shared/global-settings';
import { tapError } from '../../shared/tap-error-operator';
import { EstimateInputDebounceManager } from '../estimate-input-debounce-manager';
import { AreaEstimates } from '../models/area-estimates';
import { RevenueStateService, RootAreaState } from '../revenue-state.service';
import { BehaviorSubject } from 'rxjs';
import { UserService } from 'src/app/core-services/user.service';
import { SubsManager } from 'src/app/shared/tcc-ui/subs-manager';
import { NotificationsService } from 'src/app/shared/tcc-ui/notifications';
import { AppInsightsRef } from 'src/app/shared/tcc-ui/app-insights';
import { NavbarMessageService } from 'src/app/shared/navbar-message';

@Component({
  selector: 'app-revenue-by-bed-count',
  templateUrl: './revenue-by-bed-count.component.html',
  styles: []
})
export class RevenueByBedCountComponent implements OnInit, OnDestroy {
  private subsMgr = new SubsManager();

  /** reference to the EstimateType enum */
  readonly comments$ = this.revState.comments$;
  readonly cyLabel = `${this.globalSettings.budgetYear}`;
  readonly estimateTypeEnumRef = EstimateType;
  readonly fpOutOfSync$ = this.revState.fpOutOfSync$;
  readonly isReadOnly$ = this.revState.isReadOnly$;
  readonly org$ = this.revState.org$;
  readonly specialAccounts = this.globalSettings.specialAccounts;
  readonly state$ = this.revState.areas$.pipe(
    filter((x): x is RootAreaState => !!x),
    map(({ areas }) => (areas && areas.length) ? 'ready' : 'loading'),
    startWith('loading'));
  readonly inputBounceMgr = new EstimateInputDebounceManager(this.revState.periodSettings);
  readonly tgtPeriodIndex = this.revState.periodSettings.cyJan.index;
  readonly trayRequest$ = this.trayStateSvc.trayChanges$;
  userChangeSubject = new BehaviorSubject<UserStateInfo | undefined>(undefined);

  amenityAverages: { amenityName: string; pyAvg: number; cyAvg: number; count: number; }[] | undefined;

  commentAccount = this.specialAccounts.gpr;
  
  
  bedroomsCountTypes: AreaEstimates[] | undefined;
  showUnitTypeEstimates = true;
  showBedroomCountEstimates = true;
  private isDestroyed = false;
  private initializedDefaultEstimatesForOrgId: number | undefined;

  private numberOfBedroomCountsOriginal$ =  this.revState.numberOfBedroomCountsArea$.pipe(
    map(areaEstimates => areaEstimates.filter(ae => ae.areaType === RevenueAreaType.NumberOfBedrooms)),
    tap(x => this.bedroomsCountTypes = x)
  ).subscribe();
  
  /** estimates on the org level */
  orgArea: AreaEstimates | undefined;

  constructor(
    private aiSvc: AppInsightsRef,
    private commentsState: CommentsStateService,
    @Inject(GLOBAL) private globalSettings: IGlobalSettings,
    private notifySvc: NotificationsService,
    public revState: RevenueStateService,
    private route: ActivatedRoute,
    private trayStateSvc: MenuTrayStateService,
    private navbarMessageService: NavbarMessageService,
    private userSvc: UserService,
  ) {
  }

  ngOnInit() {
    this.isDestroyed = false;
    this.inputBounceMgr.defaultAppliedOn = this.revState.periodSettings.cyJan.value;

    this.subsMgr.addSub = this.userSvc.currentUser$.pipe(
      filter((u): u is Required<UserStateInfo> => !!u?.email),
      tap(u => this.aiSvc.appInsights?.setAuthenticatedUserContext(u.email))
    ).subscribe((u) => {
      this.userChangeSubject.next(u);
    },
      err => {
        alert('Error Authenticating');
      });

    this.subsMgr.addSub = this.route.params.pipe(
      filter((x): x is { orgId: string } => typeof x.orgId === 'string'),
      tap(x => {
        const orgId = parseInt(x.orgId, 10);
        this.revState.orgId = orgId;
      }))
      .subscribe();

    this.subsMgr.addSub = this.revState.areas$.pipe(
      filter((x): x is RootAreaState => !!x),
      tap(({ typeMap }) => {        
        const self = typeMap.get(RevenueAreaType.Self)?.[0];
        this.orgArea = typeMap.get(RevenueAreaType.Self)?.[0];
        this.inputBounceMgr.defaultRevAreaId = (this.orgArea) ? this.orgArea.revAreaId : undefined;
        this.queueDefaultEstimateInitialization(typeMap);
      })
    ).subscribe();

    // this stream saves changes
    this.subsMgr.addSub = this.inputBounceMgr.estimateChange$.pipe(
      tap(x => this.revState.enqueueEstimateSave(x.value.revAreaId!, {
        appliedOn: x.value.appliedOn!,
        estimateType: x.value.estimateType!,
        estimateId: 0,
        value: x.value.value
      })),
      tapError(() => this.notifySvc.addError('Unable to save estimates.  Please refresh and try again.'))
    ).subscribe();
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.trayStateSvc.closeTray(true);
    this.inputBounceMgr.clear();
    this.subsMgr.onDestroy();
    this.revState.orgId = undefined;
  }



  getTargetEstimateClass(estimateType: EstimateType) {
    const estTgtDiff = this.estimateVsTarget(estimateType);
    return estTgtDiff < -.01 ? 'text-danger' : estTgtDiff > .01 ? 'text-info' : '';
  }

  showComments(accountCode: string) {
    this.commentAccount = accountCode;
    this.commentsState.selectedAccountCode = accountCode;
    this.trayStateSvc.openTray('CommentManagement');
  }

  updateEstimatePct(estimateType: EstimateType, rawValue: string) {
    try {
      this.inputBounceMgr.updateRawEstimatePct(rawValue, { estimateType });
    }
    catch {
      this.notifySvc.addError(`Invalid value for estimate: ${rawValue}.  Nothing was saved.`);
    }
  }

  updateEstimateForce(estimateType: EstimateType) {
    this.inputBounceMgr.updateForce({ estimateType });
  }

  updateFp() {
    this.revState.updateFp();
  }

  toggleUnitTypeEstimates() {
    if (this.showUnitTypeEstimates && !this.showBedroomCountEstimates) {
      return;
    }

    this.showUnitTypeEstimates = !this.showUnitTypeEstimates;
  }

  toggleBedroomCountEstimates() {
    if (this.showBedroomCountEstimates && !this.showUnitTypeEstimates) {
      return;
    }

    this.showBedroomCountEstimates = !this.showBedroomCountEstimates;
  }

  updateNewLeasesFromOccupancyTarget() {
    if (!this.orgArea || !this.orgArea.children) {
      return;
    }

    const tgtOccPct = (this.orgArea.periods[this.tgtPeriodIndex].tgtOccPct || 0);
    for (const ut of this.orgArea.children) {
      for (let pIdx = this.tgtPeriodIndex; pIdx < this.revState.periodSettings.periods.length; pIdx++) {
        const appliedOn = this.revState.periodSettings.periods[pIdx].value;
        const period = ut.periods[pIdx];
        const totalOccUnits = tgtOccPct * (ut.periods[pIdx].unitCount || 0);
        // subtract renewal leases from occupied units to get new lease.  Don't go negative
        const newNewLeases = +Math.max(0, totalOccUnits - period.estRenewalLeases! || 0).toFixed(2);
        this.inputBounceMgr.updateEstimate(newNewLeases, {
          revAreaId: ut.revAreaId,
          estimateType: EstimateType.EstNewLeases,
          appliedOn
        }, { forceUpdate: true });
      }
    }
  }

  updateRenewalLeasesFromRenewalTarget() {
    if (!this.orgArea || !this.orgArea.children) {
      return;
    }
    const renewalPct = (this.orgArea.periods[this.tgtPeriodIndex].tgtRenewalPct || 0);
    
    for (const ut of this.orgArea.children) {
      for (let pIdx = this.tgtPeriodIndex; pIdx < this.revState.periodSettings.periods.length; pIdx++) {
        const appliedOn = this.revState.periodSettings.periods[pIdx].value;
        const period = ut.periods[pIdx];
        const estNewLeases = (period.estNewLeases || 0);
        const totalLeasedUnits = estNewLeases + (period.estRenewalLeases || 0);
        let newRenewalLeases = (renewalPct * (ut.periods[pIdx].unitCount || 0));

        if (totalLeasedUnits > 0) {
          const newNewLeases = +Math.max(0, totalLeasedUnits - newRenewalLeases).toFixed(2);
          this.inputBounceMgr.updateEstimate(newNewLeases, {
            revAreaId: ut.revAreaId,
            estimateType: EstimateType.EstNewLeases,
            appliedOn
          }, { forceUpdate: true });
        }

        newRenewalLeases = +newRenewalLeases.toFixed(2);
        this.inputBounceMgr.updateEstimate(newRenewalLeases, {
          revAreaId: ut.revAreaId,
          estimateType: EstimateType.EstRenewalLeases,
          appliedOn
        }, { forceUpdate: true });
      }
    }
  }

  /** updates new rates based on tgtRevenueIncreasePct */
  updateNewRates() {
    if (!this.orgArea || !this.orgArea.children) {
      return;
    }
    const tgtPeriod = this.orgArea.periods[this.tgtPeriodIndex];
    const rateIncrease = tgtPeriod.tgtRateIncreasePct || 0;
    for (const ut of this.orgArea.children) {
      // revenue increase is based on the period before.
      const estimates = ut.periods[this.tgtPeriodIndex - 1];
      const baseRate = estimates.calcAvgRate || estimates.aggs.avg?.origFpRate || 0;
      const newRate = baseRate * (1 + rateIncrease);

      this.inputBounceMgr.updateEstimate(newRate, { revAreaId: ut.revAreaId, estimateType: EstimateType.EstNewRate },
        { forceUpdate: true, forCy: true });
    }
  }
  /**
   * Takes an estimate that's a percentage of a unit type and converts it to a real number
   */
  updateAreaUnitCountPctEstimate(area: AreaEstimates, estimateType: EstimateType, pctVal: number) {
    for (let pIdx = this.tgtPeriodIndex; pIdx < this.revState.periodSettings.periods.length; pIdx++) {
      const appliedOn = this.revState.periodSettings.periods[pIdx].value;
      const value = +(pctVal * area.periods[pIdx].unitCount! || 0).toFixed(2);
      this.inputBounceMgr.updateEstimate(value, { revAreaId: area.revAreaId, estimateType, appliedOn }, { forceUpdate: true });
    }
  }

  /** Returns the difference between estimate vs Target, with a positive number meaning that the estimate exceeds the target. */
  private estimateVsTarget(estimateType: EstimateType) {
    let targetAmt = 0;
    let estimateAmount = 0;
    switch (estimateType) {
      case EstimateType.TargetRenewalPct:
        targetAmt = this.orgArea?.periods[this.tgtPeriodIndex].tgtRenewalPct || 0;
        estimateAmount = this.orgArea?.summary?.estOccRenewalPct || 0;
        break;
      case EstimateType.TargetOccupancyPct:
        targetAmt = this.orgArea?.periods[this.tgtPeriodIndex].tgtOccPct || 0;
        estimateAmount = this.orgArea?.summary?.estOccPct || 0;
        break;
      case EstimateType.TargetRateIncreasePct:
        targetAmt = this.orgArea?.periods[this.tgtPeriodIndex].tgtRateIncreasePct || 0;
        estimateAmount = this.orgArea?.summary?.estRateGrowth || 0;
        break;
    }
    return estimateAmount - targetAmt;
  }

  private queueDefaultEstimateInitialization(typeMap: RootAreaState['typeMap']) {
    
    const orgId = this.revState.orgId;
    if (!orgId || this.initializedDefaultEstimatesForOrgId === orgId) {      
      return;
    }

    const amenities = typeMap.get(RevenueAreaType.AddOn) || [];
    const unitTypes = typeMap.get(RevenueAreaType.UnitType) || [];
    if (amenities.length === 0 && unitTypes.length === 0) {
      return; // ani't no use if there aren't any
    }

    const missingAmenityDefaults = this.getMissingDefaultNewRates(
                                          amenities,
                                          (amenity, periodIndex) => amenity.periods[periodIndex].origFpRate
                                        );
    const missingUnitTypeDefaults = this.getMissingDefaultNewRates(
                                          unitTypes,
                                          (ut) => ut.periods[this.tgtPeriodIndex].aggs.avg?.estRenewalRate
                                        );
    const missingDefaults = missingAmenityDefaults.concat(missingUnitTypeDefaults);
    if (missingDefaults.length === 0) {
      this.initializedDefaultEstimatesForOrgId = orgId;
      return;
    }

    this.initializedDefaultEstimatesForOrgId = orgId;
    setTimeout(async () => { // this helps not to freeze the browser
      if (this.isDestroyed) {
        return;
      }
      this.navbarMessageService.setMessage('Initializing default estimates...');
      try {
        await this.initializeMissingDefaultNewRates(missingDefaults);
      }
      finally {
        this.navbarMessageService.clearMessage();
      }   
    });
  }

  private getMissingDefaultNewRates(
    areas: AreaEstimates[],
    getDefaultValue: (area: AreaEstimates, periodIndex: number) => number | undefined
  ) {
    const missingDefaults: { area: AreaEstimates; periodIndex: number; value: number }[] = [];

    for (const area of areas) {
      for (let i = this.tgtPeriodIndex; i < this.revState.periodSettings.periods.length; i++) {
        if (area.periods[i].estNewRate != null) {
          continue;
        }

        const value = getDefaultValue(area, i);
        if (value == null) {
          continue;
        }

        missingDefaults.push({ area, periodIndex: i, value });
      }
    }

    return missingDefaults;
  }

  private async yieldToBrowser() { // this helps not to freeze the browser
    await new Promise(resolve => setTimeout(resolve));
  }

  private async initializeMissingDefaultNewRates(defaults: { area: AreaEstimates; periodIndex: number; value: number }[]) {
    let changesQueued = 0;
    for (const item of defaults) {
      if (this.isDestroyed) {
        return;
      }
      this.navbarMessageService.setMessage(`Initializing default estimates... (${changesQueued + 1} of ${defaults.length})`);
      this.revState.enqueueEstimateSave(item.area.revAreaId, {
        appliedOn: this.revState.periodSettings.periods[item.periodIndex].value,
        estimateType: EstimateType.EstNewRate,
        estimateId: 0,
        value: item.value
      }, undefined, true);

      changesQueued++;
      if (changesQueued % 25 === 0) { // when the changesQueued is a multiple of 25
        // let the user work without the UI freezing, 
        // there could be a lag but it supposed to wait until the user is idel then continue 
        await this.yieldToBrowser();  
      }    
    }
  }
}


